import type { LiveTruck } from '@chukua/common';
import { X } from 'lucide-react';
import { Link } from 'react-router';
import { dispatchPaths } from '../../dispatch/dispatch.paths';
import { truckTabPath } from '../../fleet/features/trucks/trucks.paths';
import { truckAgeLabel } from '../policies/truck-age-label';
import { truckMapStatus, TRUCK_MAP_STATUS_LABELS } from '../policies/truck-map-status';

interface TruckPanelProps {
  truck: LiveTruck;
  now: Date;
  onClose: () => void;
}

/** The truck that was clicked: where it stands, how recent that is, and the way on to its trip (or, with none, to the truck). */
export function TruckPanel({ truck, now, onClose }: TruckPanelProps) {
  const { fix, trip } = truck;
  const speedKmh = fix.speedMps === null ? null : Math.round(fix.speedMps * 3.6);
  return (
    <aside aria-label={`Truck ${truck.registration}`} className="w-72 rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-fg">{truck.registration}</h2>
          <p className="text-xs text-muted">
            {TRUCK_MAP_STATUS_LABELS[truckMapStatus(fix, now, truck.reportIntervalSeconds)]} · {truckAgeLabel(fix.deviceTime, now)}
          </p>
        </div>
        <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 text-muted hover:text-fg">
          <X className="size-4" />
        </button>
      </div>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <dt className="text-muted">Speed</dt>
        <dd className="text-fg">{speedKmh === null ? 'Not reported' : `${speedKmh} km/h`}</dd>
        {trip && (
          <>
            <dt className="text-muted">Customer</dt>
            <dd className="text-fg">{trip.customerName}</dd>
            <dt className="text-muted">Route</dt>
            <dd className="text-fg">
              {trip.originZoneCode ?? '—'} → {trip.destinationZoneCode ?? '—'}
            </dd>
          </>
        )}
      </dl>
      <div className="mt-3 text-sm">
        {trip ? (
          <Link className="text-link hover:underline" to={dispatchPaths.dispatchTrip(trip.id)}>
            Open trip {trip.number}
          </Link>
        ) : (
          <>
            <p className="text-muted">Not on a trip.</p>
            <Link className="text-link hover:underline" to={truckTabPath(truck.truckId, 'details')}>
              Open truck
            </Link>
          </>
        )}
      </div>
    </aside>
  );
}
